import React, { useState, useEffect, useRef } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Header from "../Header/Header";
import {
  Button,
  CircularProgress,
  Grid,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  ListItem,
  List
} from "@mui/material";
import batchService from "../../services/BatchService";
import { EmployeeSyncUserDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import EmployeeSiteSelector from "./EmployeeSiteSelector";
import BaseDialog from "../Common/BaseDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import moment from "moment";
import { CSVLink } from "react-csv";
import { SubHeader } from "../Common/Layout/SubHeader";
import { getStyles } from './styles';

const TextLabel = styled(Typography)(({ theme }) => ({
  paddingRight: theme.spacing(1),
  fontSize: "18px",
}));

const GridContainer = styled(Grid)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  width: '100%',
}));

const TextContainer = styled(Grid)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(1)}`,
  width: '100%',
}));

const StandardSiteSelector = styled(EmployeeSiteSelector)({
  width: '67%',
});

export default function EmployeeBatch() {
  const [selectedSite, setSelectedSite] = useState<SiteDTO | undefined>(
    undefined
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState<EmployeeSyncUserDTO[] | undefined>(undefined);
  const [fileName, setFileName] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [disabledSave, setDisabledSave] = useState(false);
  const [loginSuffix, setLoginSuffix] = useState(true)

  const theme = useTheme();
  const styles = getStyles(theme);
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    async function checkBatchStatus(iteration: number) {
      if (iteration > 10) {
        alert(
          "Batch processing execution time has exceeded the maximum wait time. Please try again later. You may also contact Technical Support for further help."
        );
        return;
      }
      const batchInProgress = await batchService.checkEmployeeBatchInProgress(
        selectedSite!.id
      );
      if (!batchInProgress) {
        if (iteration > 0) {
          alert("Previous batch complete.");
        }
        setDisabledSave(false);
      } else {
        setDisabledSave(true);
        timer.current = setTimeout(() => checkBatchStatus(++iteration), 60000);
      }
    }

    if (selectedSite) {
      checkBatchStatus(0);
    }
  }, [selectedSite]);

  const handleChangeSite = (newSite: SiteDTO) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    setSelectedSite(newSite);

    const evalRegex = /eval([0-9]+)?/g;
    if (newSite) {
      setDisabledSave(false);
      setLoginSuffix(!(newSite.subdomain === 'training' || newSite.subdomain.match(evalRegex)));
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    setFileName("");
    const results = await batchService.createEmployeeBatch(
      selectedSite!,
      firstName,
      lastName,
      !loginSuffix
    );
    if (results) {
      setUsers(results);
      setFileName(
        `${firstName.substring(0, 1)}${lastName}_${moment().format(
          "YYYY-MM-DD"
        )}.csv`
      );
      setSiteUrl(`${selectedSite!.subdomain}.login.beable.com`);
      setDialogOpen(true);
    } else {
      alert(`Error rostering Beable user account`);
    }
    setSubmitting(false);
  };

  const getCsvData = () => {
    const data = users
      ? users.map((user: EmployeeSyncUserDTO) => {
          return {
            url: siteUrl,
            username: user.userName,
            password: user.password,
            firstName: user.firstName,
            lastName: user.familyName,
            grade: user.grades,
            language: user.primaryLanguage,
            role: user.role,
            lexile: user.initialLexile,
          };
        })
      : [];

    return data;
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginSuffix(event.target.checked);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFirstName("");
    setLastName("");
    setSelectedSite(undefined);
  };

  const requiredFieldsFilled = () => {
    return selectedSite && firstName && lastName;
  };

  return (
    <>
      <Header />
      <SubHeader>
        <Typography variant="h6" component="span">
          Roster Beable User Accounts
        </Typography>
      </SubHeader>
      <Box sx={styles.root}>
        <Grid container>
          <Grid size={12}>
            <Typography variant="body1">
              This feature allows you to generate a roster file for a new
              employee, evaluator, or training.
            </Typography>
            <List sx={{ marginTop: 0, fontSize: "18px", listStyleType: "disc", pl: 4 }}>
              <ListItem sx={{ display: "list-item", padding: "0px 16px" }}>
                Use <Typography component="span" sx={{ fontStyle: "italic", margin: '0 4px' }}>Presenter</Typography> for
                Sales, Marketing, Executive Team, Implementation, Finance, and
                Content.
              </ListItem>
              <ListItem sx={{ display: "list-item", padding: "0px 16px" }}>
                Use <Typography component="span" sx={{ fontStyle: "italic", margin: '0 4px' }}>Employee</Typography> for
                Product, Dev, QA, and Support.
              </ListItem>
            </List>
            <Typography variant="body1" sx={{ paddingBottom: '10px' }}>
              After processing, you can download a .csv file with the usernames
              and passwords. The naming convention always uses first initial,
              last name, grade, role, language [en/es], Lexile range (o,a,b) for
              student accounts only, and 2 random special characters.
              {/*Only suppress special characters for evaluator and training accounts.*/}
            </Typography>
          </Grid>
          <Grid container size={12} sx={{
            justifyContent: "center"
          }}>
            <Grid size={8}>
              <Grid container>
              <GridContainer container size={12} sx={{ justifyContent: 'flex-start' }}>
                <Grid size={12}>
                  <StandardSiteSelector
                    onSelect={handleChangeSite}
                    siteId={selectedSite?.id}
                  />
                </Grid>
              </GridContainer>
              <TextContainer container size={12} sx={{ alignItems: 'center' }}>
                <Grid size={2}>
                  <TextLabel>First Name</TextLabel>
                </Grid>
                <Grid size={6}>
                  <TextField
                    variant="outlined"
                    fullWidth={true}
                    size="small"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                </Grid>
                <Grid size={4} />
              </TextContainer>
              <TextContainer container size={12} sx={{ alignItems: 'center' }}>
                <Grid size={2}>
                  <TextLabel>Last Name</TextLabel>
                </Grid>
                <Grid size={6}>
                  <TextField
                    variant="outlined"
                    fullWidth={true}
                    size="small"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </Grid>
                <Grid size={4} />
              </TextContainer>
              <TextContainer container size={12} sx={{ justifyContent: 'flex-start' }}>
              <FormControlLabel
                      control={
                        <Checkbox
                          checked={loginSuffix}
                          onChange={handleCheckboxChange}
                          name="loginSuffix"
                          color="primary"
                        />
                      }
                      label="Include special characters in username/password"
                    />
              </TextContainer>
              <TextContainer container size={12} sx={{ justifyContent: 'center' }}>
                {submitting && (
                  <Box sx={styles.loaderContainer}>
                    <CircularProgress disableShrink />
                  </Box>
                )}
                {!submitting && disabledSave && (
                  <Box sx={styles.warning}>
                    Batch currently in progress, please wait...
                    <br />
                  </Box>
                )}
                {!submitting && (
                  <Button
                    variant="contained"
                    color="primary"
                    aria-label="save"
                    onClick={handleSave}
                    disabled={disabledSave || !requiredFieldsFilled()}
                  >
                    Save
                  </Button>
                )}
              </TextContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <BaseDialog
        open={dialogOpen}
        header="Download User Data"
        showCloseIcon={true}
        showCloseButton={false}
        onClose={() => closeDialog()}
      >
        <Box
          sx={{
            minHeight: 150,
            display: "flex",
            alignItems: "center"
          }}>
          <Box sx={{
            width: "100%"
          }}>
            <DialogContentText>
              Employee roster batch started. Click Download to download the new
              user logins.
            </DialogContentText>
            <DialogActions>
              <CSVLink data={getCsvData()} filename={fileName}>
                <Button
                  onClick={closeDialog}
                  variant="contained"
                  color="primary"
                >
                  Download
                </Button>
              </CSVLink>
              <Button
                onClick={closeDialog}
                variant="contained"
                color="secondary"
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </Box>
      </BaseDialog>
    </>
  );
}
